import { EM_DASH } from "~/lib/format";

type Scalar = string | number | boolean | null;

function isScalar(value: unknown): value is Scalar {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/** A value the server-side allowlist promised to redact down to. Any other
 *  shape renders as a fixed placeholder rather than being dumped, on the
 *  chance the allowlist ever regresses — see the module doc below. */
function renderScalar(value: unknown): string {
  if (!isScalar(value)) return "(unsupported value type — not shown)";
  if (value === null) return EM_DASH;
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

/**
 * A before/after audit row, rendered as one line per changed field.
 *
 * The server already redacts Before and After to a small per-target-type
 * allowlist (internal/service/audit/redact.go) before they ever reach this
 * screen. This component is the second, independent layer the ticket asks
 * for: it never renders a value unless it is a scalar (string, number,
 * boolean or null). An object or array — which should never arrive, but would
 * mean the allowlist regressed — prints a fixed placeholder instead of being
 * stringified, so a leaked structured secret cannot end up on screen just
 * because a server-side allowlist entry was misconfigured. The defense is on
 * value *shape*, not on a client-side copy of the server's field list, which
 * would only drift out of sync with it.
 */
export function AuditLogDiff({
  before,
  after,
}: {
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
}) {
  const keys = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
  ).sort();

  const changed = keys.filter((key) => !Object.is(before?.[key], after?.[key]));

  if (changed.length === 0) {
    return <p className="text-sm text-muted-foreground">No recorded field changes.</p>;
  }

  return (
    <dl className="space-y-1.5 text-sm">
      {changed.map((key) => {
        const hadBefore = before != null && key in before;
        const hasAfter = after != null && key in after;

        return (
          <div key={key} className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <dt className="font-medium">{key}</dt>
            <dd className="text-muted-foreground">
              {!hadBefore && hasAfter ? (
                <>
                  set to <span className="font-mono text-[0.8125rem]">{renderScalar(after![key])}</span>
                </>
              ) : hadBefore && !hasAfter ? (
                <>
                  cleared (was{" "}
                  <span className="font-mono text-[0.8125rem]">{renderScalar(before![key])}</span>)
                </>
              ) : (
                <>
                  <span className="font-mono text-[0.8125rem]">{renderScalar(before?.[key])}</span>
                  {" → "}
                  <span className="font-mono text-[0.8125rem]">{renderScalar(after?.[key])}</span>
                </>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
